import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import {
  Button,
  Empty,
  Input,
  Modal,
  Popconfirm,
  Space,
  Spin,
  Typography,
  notification,
} from 'antd';
import CustomButton from 'components/custom-button';
import { useCallback, useEffect, useState } from 'react';
import { API } from 'service/api-route';
import AxiosUtils from 'utils/axios-utils';
import { GROUP_NAME_MAX_LENGTH, MenuGroup, getErrorMessage } from '../constants';
import styles from '../styles.module.scss';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  /** Gọi sau mỗi thay đổi thành công (bảng ứng dụng bên ngoài có hiện tên nhóm). */
  onChanged: () => void;
}

const notifyError = (error: unknown, fallback: string) =>
  notification.error({
    message: 'Lỗi',
    description: getErrorMessage(error, fallback),
    duration: 4,
  });

const notifySuccess = (description: string) =>
  notification.success({ message: 'Thành công', description, duration: 3 });

const ModalMenuGroups = ({ isOpen, onClose, onChanged }: Props) => {
  const [groups, setGroups] = useState<MenuGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [newName, setNewName] = useState('');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingName, setEditingName] = useState('');
  // Mỗi lúc chỉ chạy một thao tác: đổi thứ tự gửi lên toàn bộ danh sách id,
  // nên phải tính từ danh sách mới nhất.
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const busy = busyKey !== null;

  const fetchGroups = useCallback(async () => {
    setLoading(true);
    try {
      const res: any = await AxiosUtils.get(API.menuGroups);
      setGroups(Array.isArray(res?.data) ? res.data : []);
    } catch (error) {
      notifyError(error, 'Không tải được danh sách nhóm.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) fetchGroups();
  }, [isOpen, fetchGroups]);

  const handleCreate = async () => {
    const name = newName.trim();
    if (!name || name.length > GROUP_NAME_MAX_LENGTH || busy) return;
    setBusyKey('create');
    try {
      await AxiosUtils.post(API.menuGroups, { name });
      setNewName('');
      notifySuccess(`Đã thêm nhóm “${name}”.`);
      onChanged();
      await fetchGroups();
    } catch (error) {
      notifyError(error, 'Không thêm được nhóm.');
    } finally {
      setBusyKey(null);
    }
  };

  const startEdit = (group: MenuGroup) => {
    setEditingId(group.id);
    setEditingName(group.name);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingName('');
  };

  const handleRename = async (group: MenuGroup) => {
    const name = editingName.trim();
    if (!name || name.length > GROUP_NAME_MAX_LENGTH || busy) return;
    if (name === group.name) {
      cancelEdit();
      return;
    }
    setBusyKey(`rename-${group.id}`);
    try {
      await AxiosUtils.patch(`${API.menuGroups}/${group.id}`, { name });
      setGroups((prev) => prev.map((item) => (item.id === group.id ? { ...item, name } : item)));
      cancelEdit();
      notifySuccess('Đã đổi tên nhóm.');
      onChanged();
    } catch (error) {
      notifyError(error, 'Không đổi được tên nhóm.');
    } finally {
      setBusyKey(null);
    }
  };

  const handleDelete = async (group: MenuGroup) => {
    if (busy) return;
    setBusyKey(`delete-${group.id}`);
    try {
      await AxiosUtils._delete(`${API.menuGroups}/${group.id}`, undefined);
      setGroups((prev) => prev.filter((item) => item.id !== group.id));
      notifySuccess(
        group.menuCount
          ? `Đã xóa nhóm “${group.name}”. ${group.menuCount} ứng dụng đã chuyển sang mục Khác.`
          : `Đã xóa nhóm “${group.name}”.`
      );
      onChanged();
    } catch (error) {
      notifyError(error, 'Không xóa được nhóm.');
    } finally {
      setBusyKey(null);
    }
  };

  const handleMove = async (index: number, offset: -1 | 1) => {
    const target = index + offset;
    if (busy || target < 0 || target >= groups.length) return;
    const reordered = [...groups];
    [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
    setBusyKey(`${offset < 0 ? 'up' : 'down'}-${groups[index].id}`);
    try {
      const res: any = await AxiosUtils.put(API.menuGroupsOrder, {
        ids: reordered.map((item) => item.id),
      });
      // Server trả lại danh sách nhóm đã sắp; phòng khi không có thì dùng thứ tự vừa gửi.
      setGroups(Array.isArray(res?.data) ? res.data : reordered);
      onChanged();
    } catch (error) {
      notifyError(error, 'Không đổi được thứ tự nhóm.');
    } finally {
      setBusyKey(null);
    }
  };

  const renderGroup = (group: MenuGroup, index: number) => {
    if (editingId === group.id) {
      const saving = busyKey === `rename-${group.id}`;
      return (
        <div key={group.id} className={styles.groupRow}>
          <span className={styles.groupIndex}>{index + 1}</span>
          <div className={styles.groupEdit}>
            <div className={styles.groupInput}>
              <Input
                autoFocus
                value={editingName}
                maxLength={GROUP_NAME_MAX_LENGTH}
                showCount
                onChange={(e) => setEditingName(e.target.value)}
                onPressEnter={() => handleRename(group)}
                onKeyDown={(e) => {
                  // Esc chỉ hủy sửa tên, không đóng cả hộp thoại.
                  if (e.key === 'Escape') {
                    e.stopPropagation();
                    cancelEdit();
                  }
                }}
              />
            </div>
            <Button
              type="primary"
              loading={saving}
              disabled={!editingName.trim() || (busy && !saving)}
              onClick={() => handleRename(group)}
            >
              Lưu
            </Button>
            <Button disabled={saving} onClick={cancelEdit}>
              Hủy
            </Button>
          </div>
        </div>
      );
    }

    const upKey = `up-${group.id}`;
    const downKey = `down-${group.id}`;
    const deleteKey = `delete-${group.id}`;
    return (
      <div key={group.id} className={styles.groupRow}>
        <span className={styles.groupIndex}>{index + 1}</span>
        <div className={styles.groupInfo}>
          <div className={styles.groupName}>{group.name}</div>
          <Typography.Text type="secondary">{group.menuCount ?? 0} ứng dụng</Typography.Text>
        </div>
        <Space size={4} wrap>
          <Button
            icon={<ArrowUpOutlined />}
            title="Chuyển lên"
            aria-label="Chuyển lên"
            loading={busyKey === upKey}
            disabled={index === 0 || (busy && busyKey !== upKey)}
            onClick={() => handleMove(index, -1)}
          />
          <Button
            icon={<ArrowDownOutlined />}
            title="Chuyển xuống"
            aria-label="Chuyển xuống"
            loading={busyKey === downKey}
            disabled={index === groups.length - 1 || (busy && busyKey !== downKey)}
            onClick={() => handleMove(index, 1)}
          />
          <Button icon={<EditOutlined />} disabled={busy} onClick={() => startEdit(group)}>
            Sửa tên
          </Button>
          <Popconfirm
            title="Xóa nhóm này?"
            description="Các ứng dụng trong nhóm sẽ chuyển sang mục Khác, không bị xóa."
            okText="Xóa"
            cancelText="Hủy"
            okButtonProps={{ danger: true }}
            disabled={busy}
            onConfirm={() => handleDelete(group)}
          >
            <Button
              danger
              icon={<DeleteOutlined />}
              loading={busyKey === deleteKey}
              disabled={busy && busyKey !== deleteKey}
            >
              Xóa
            </Button>
          </Popconfirm>
        </Space>
      </div>
    );
  };

  return (
    <Modal
      open={isOpen}
      title="Nhóm ứng dụng"
      onCancel={onClose}
      width={640}
      footer={
        <CustomButton text="Đóng" bgColor="primary-grey" htmlType="button" onClick={onClose} />
      }
    >
      <Typography.Text type="secondary" className={styles.groupHint}>
        Thứ tự dưới đây là thứ tự các nhóm trên màn “Tất cả ứng dụng” của app.
      </Typography.Text>
      <div className={styles.groupCreate}>
        <div className={styles.groupInput}>
          <Input
            size="large"
            placeholder="Tên nhóm mới, ví dụ: Quy định & hỗ trợ"
            value={newName}
            maxLength={GROUP_NAME_MAX_LENGTH}
            showCount
            onChange={(e) => setNewName(e.target.value)}
            onPressEnter={handleCreate}
          />
        </div>
        <CustomButton
          bgColor="primary-green"
          text="Thêm nhóm"
          icon={<PlusOutlined />}
          htmlType="button"
          loading={busyKey === 'create'}
          disabled={!newName.trim() || (busy && busyKey !== 'create')}
          onClick={handleCreate}
        />
      </div>
      <Spin spinning={loading}>
        <div className={styles.groupBody}>
          {groups.length > 0
            ? groups.map(renderGroup)
            : !loading && (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="Chưa có nhóm nào. Mọi ứng dụng đang nằm ở mục “Khác”."
                />
              )}
        </div>
      </Spin>
    </Modal>
  );
};

export default ModalMenuGroups;
